import { useEffect, useRef, useState } from "react";
import { getEventCounter } from "@/lib/api";
import { TID } from "@/constants/testIds";
import { TrendingUp, Users } from "lucide-react";

/**
 * RegistrationsTicker — social-proof counter for the Chapter III launch.
 * • Fetches server-side count on mount
 * • Refetches every 60s so ALL visitors converge on the same number
 * • Between fetches, applies gentle local +1 bumps every 60-120s so the number
 *   feels alive during a single visitor's session
 */
export default function RegistrationsTicker({ variant = "light", className = "" }) {
  const [data, setData] = useState(null);
  const displayRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let poll;
    let bump;

    async function fetchOnce() {
      try {
        const c = await getEventCounter();
        if (!cancelled) setData(c);
      } catch { /* ignore transient errors */ }
    }

    fetchOnce();
    poll = setInterval(fetchOnce, 60_000);
    // Local visual bump every ~75-120 seconds
    function scheduleBump() {
      const delay = 60_000 + Math.random() * 60_000;
      bump = setTimeout(() => {
        setData((prev) => prev ? {
          ...prev,
          registered: prev.registered + 1,
          remaining: Math.max(0, prev.remaining - 1),
        } : prev);
        scheduleBump();
      }, delay);
    }
    scheduleBump();

    return () => {
      cancelled = true;
      clearInterval(poll);
      clearTimeout(bump);
    };
  }, []);

  // Flip animation on the digits when count changes
  const lastRegistered = useRef(null);
  useEffect(() => {
    if (!displayRef.current || !data) return;
    if (lastRegistered.current === data.registered) return;
    lastRegistered.current = data.registered;
    displayRef.current.animate(
      [
        { transform: "translateY(6px)", opacity: 0.4 },
        { transform: "translateY(0)", opacity: 1 },
      ],
      { duration: 380, easing: "cubic-bezier(0.22, 1, 0.36, 1)" }
    );
  }, [data]);

  const dark = variant === "dark";
  const textCol = dark ? "text-cream" : "text-forest";
  const dimCol = dark ? "text-cream/60" : "text-forest/60";
  const accent = dark ? "text-lime" : "text-teal";

  if (!data) {
    return (
      <div className={`inline-flex items-center gap-3 ${dimCol} ${className}`}>
        <span className="w-2 h-2 rounded-full bg-lime animate-pulse" />
        <span className="text-sm">Loading live count…</span>
      </div>
    );
  }

  const pct = Math.min(100, Math.round((data.registered / data.capacity) * 100));

  return (
    <div
      data-testid={TID.registrationsTicker}
      className={`inline-flex flex-col gap-3 ${className}`}
    >
      <div className={`flex items-baseline gap-3 ${textCol}`}>
        <Users size={22} className={accent} />
        <span
          ref={displayRef}
          className="font-display text-5xl md:text-7xl leading-none tabular-nums tracking-tight"
        >
          {data.registered.toLocaleString("en-IN")}
        </span>
        <span className={`eyebrow ${dimCol} pb-1.5`}>already registered</span>
      </div>
      <div className={`flex items-center gap-4 text-xs ${dimCol}`}>
        <span className="inline-flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-lime opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-lime" />
          </span>
          Live · updates every 90s
        </span>
        <span>·</span>
        <span data-testid={TID.registrationsRemaining} className={`${accent} font-semibold`}>
          Only {data.remaining.toLocaleString("en-IN")} slots left
        </span>
      </div>
      <div className={`h-1.5 rounded-full overflow-hidden ${dark ? "bg-cream/15" : "bg-forest/10"} max-w-md`}>
        <div
          className="h-full rounded-full bg-gradient-to-r from-teal to-lime transition-[width] duration-1000"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className={`text-[11px] ${dimCol}`}>
        <TrendingUp size={11} className="inline mr-1 -mt-0.5" />
        {pct}% of {data.capacity.toLocaleString("en-IN")} launch invites confirmed
      </div>
    </div>
  );
}
